/* eslint-disable import/no-internal-modules */
import { Button } from '@studio/design-system/button';
import { Table } from '@studio/design-system/table';
import { dayjs } from '@studio/js-utils/time';
import { clsx } from 'cn';
import { Error } from './components/error.jsx';
import type { BillingInfo } from './types.js';
import { ElectronAPI } from '#~/electron/index.js';
import { useAnalytics } from '#~/analytics/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';

/** Status shown while Stripe is still preparing an invoice. */
const PENDING_STATUS = 'Pending';

/** Explains why View is disabled on a pending invoice. */
const PENDING_VIEW_TITLE = 'Available once Stripe finalizes this invoice';

/** Invoices component. */
export const InvoicesSection = ({
  billing,
  onPayOverage
}: {
  billing: BillingInfo;
  onPayOverage: () => void;
}) => {
  const { track } = useAnalytics();

  return (
    <div className='rounded-8 bg-dark'>
      <div className='flex items-center gap-8 p-20'>
        <div className='mb-0 text-lg font-demibold leading-100'>Invoices</div>
        <div className='grow' />
        {!!billing.isOverdue && (
          <div className='flex items-center gap-x-8'>
            <Error>You have an overdue invoice. Please pay now or risk downgrade</Error>
            <Button onClick={onPayOverage}>Pay Now</Button>
          </div>
        )}
      </div>
      <Table className='[&_td:first-child]:pl-32 [&_th:first-child]:pl-32'>
        <thead>
          <tr>
            <th className='font-sans font-regular'>Invoice</th>
            <th className='font-sans font-regular'>Plan</th>
            <th className='font-sans font-regular'>Invoice Date</th>
            <th className='font-sans font-regular'>Total</th>
            <th className='font-sans font-regular'>Status</th>
            <th className='font-sans font-regular'>Options</th>
          </tr>
        </thead>
        <tbody className='text-muted'>
          {billing.invoices.map((invoice) => {
            // Stripe issues the invoice link at finalization, so a draft has none yet.
            const isPending = invoice.url === '';
            const status = isPending ? PENDING_STATUS : invoice.status;

            return (
              <tr key={invoice.id}>
                <td className='max-w-[75px] text-foreground'>
                  <div className='flex items-center gap-0 text-sm'>
                    <i className='i-mdi-file-outline' />
                    <div className='overflow-hidden truncate overflow-ellipsis whitespace-nowrap'>
                      {invoice.id}
                    </div>
                  </div>
                </td>
                <td className='w-[100px] capitalize'>{invoice.plan} Plan</td>
                <td>{dayjs(invoice.period).format('MMM DD, YYYY')}</td>
                <td>
                  {invoice.total.currency}
                  {invoice.total.value}
                </td>
                <td>
                  <div
                    className={clsx('flex items-center gap-4', {
                      'text-primary': !isPending,
                      'text-muted': isPending,
                      'text-error': status.toLowerCase() === 'overdue',
                      'text-success': status.toLowerCase() === 'paid'
                    })}
                  >
                    <i
                      className={clsx({
                        'i-mdi-clock-outline': isPending,
                        'i-mdi-bell-outline': status.toLowerCase() === 'upcoming',
                        'i-mdi-check-circle-outline': status.toLowerCase() === 'paid',
                        'i-mdi-info-circle-outline': status.toLowerCase() === 'overdue'
                      })}
                    />
                    <span className='capitalize'>{status}</span>
                  </div>
                </td>
                <td>
                  {isPending ? (
                    // Per ~/AGENTS.md, shared controls keep unavailable invoice actions consistent.
                    <Button
                      type='button'
                      variant='link'
                      disabled
                      title={PENDING_VIEW_TITLE}
                      className='h-auto cursor-not-allowed p-0 font-regular text-muted no-underline'
                    >
                      View
                    </Button>
                  ) : (
                    <a
                      href={'#'}
                      onClick={(e) => {
                        e.preventDefault();
                        // Track invoice download/view event
                        track(EVENT_NAMES.CLICK_DOWNLOAD_INVOICE, EVENT_CATEGORIES.BILLING, {
                          invoiceId: invoice.id,
                          planType: invoice.plan.toLowerCase()
                        });
                        ElectronAPI.openUrl(invoice.url);
                      }}
                      className='text-foreground underline'
                    >
                      {invoice.status.toLowerCase() === 'overdue' ? 'Pay Now' : 'View'}
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
